import React, { useEffect, useState } from "react";
import {
    Col,
    DatePicker,
    Form,
    Input,
    Modal,
    Row,
    Select,
    message,
    notification,
} from "antd";
import styled from "styled-components";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

const { Option } = Select;


function findWeeksInRange(startDate, endDate) {
    if (!startDate || !endDate) return [];

    const weeks = [];
    let current = dayjs(startDate).startOf("isoWeek");
    const end = dayjs(endDate);

    while (current.isSameOrBefore(end, "day")) {
        weeks.push({
            weekId: current.format("YYYY") + "W" + current.isoWeek(),
            bookedStartDate: current.startOf("isoWeek").format("YYYY-MM-DD"),
            bookedEndDate: current.endOf("isoWeek").format("YYYY-MM-DD"),
        });

        current = current.add(1, "week").startOf("isoWeek");
    }

    return weeks;
}

const getWorkingDays = (start, end) => {
    if (!start || !end) return 0;
    let count = 0;
    let current = dayjs(start);
    const endDay = dayjs(end);
    while (current.isSameOrBefore(endDay, "day")) {
        const d = current.day();
        if (d !== 0 && d !== 6) count++;
        current = current.add(1, "day");
    }
    return count;
};

// FIX 16: Remove box-shadow from subcontractor / resource modal
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    /* FIX 16: Remove shadow */
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  }
  .ant-modal-header {
    border-bottom: none;
    padding: 20px 24px 10px;
  }
  .ant-modal-title {
    font-weight: 600;
    font-size: 18px;
  }
  .ant-modal-body {
    padding: 10px 24px 20px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-form-item-label > label {
    font-weight: 500;
    color: #5d5f68;
  }
  .ant-input,
  .ant-picker,
  .ant-select-selector {
    height: 40px !important;
    border-radius: 6px !important;
  }
  .ant-picker {
    width: 100%;
  }
  .ant-select-selector {
    display: flex;
    align-items: center;
  }
`;

const ModalBody = styled.div`
  padding: 0;

  .mandays-label {
    font-size: 14px;
    font-weight: 600;
    color: #5d5f68;
    margin: 8px 0 16px;
    display: block;
  }

  .footer-btns {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
  }

  .btn-cancel {
    border-radius: 6px;
    border: 1px solid #000000;
    background: #fff;
    color: #000000;
    font-weight: 500;
    height: 36px;
    padding: 0 18px;
    cursor: pointer;
  }

  .btn-cancel:hover {
    border-color: #999;
  }

  .btn-proceed {
    border-radius: 6px;
    background: #00685F;
    border: none;
    color: #ffffff;
    font-weight: 600;
    height: 36px;
    padding: 0 22px;
    cursor: pointer;
  }

  .btn-proceed:hover {
    background: #00685F;
    color: #ffffff;

  }
`;

// FIX: spelling now matches the check performed in resolveRole ("Referring Attorney")
const ROLE_OPTIONS = [
    { value: "Lead Attorney", label: "Lead Attorney" },
    { value: "Referring Attorney", label: "Referring Attorney" },
    { value: "Member", label: "Member" },
];

const JUSTIFICATION_OPTIONS = [
    "Availability",
    "Expense/Experience",
    "Subject Matter Expert",
    "Cost/Value",
    "Client Experience/History",
    "Technical/Education Qualification",
];

// Safe dayjs conversion helper
const toSafeDayjs = (val) => {
    if (!val) return null;
    if (val && typeof val === "object" && typeof val.isValid === "function") {
        return val.isValid() ? val : null;
    }
    const d = dayjs(val);
    return d.isValid() ? d : null;
};


const AddResourceModal = ({
    open,
    onClose,
    selectedResource,
    editingResource,
    onAdd,
    onUpdate,
    fetchResourceList,
    matterData,
    fromAddResourcePage,
    planStartDate,
    planEndDate,
    leadAttorneyName,
    isActiveMatter
}) => {
    const [form] = Form.useForm();
    const [workingDays, setWorkingDays] = useState(0);
    const [loading, setLoading] = useState(false);
    const [startDateValue, setStartDateValue] = useState(null); // tracks selected start for end-picker navigation
    const location = useLocation();
    const userInfo = useSelector(getUserInfo);

    // FIX: Referring Attorney (and any other role) now resolves correctly even when
    // the resource has never been booked before (i.e. not present in stateResourceList).
    // We fall back to the resource's own role fields instead of silently defaulting to "Member".
    const resolveRole = (resource, resourceList = []) => {
        const match = resourceList.find((r) => r.userId === resource?.userId);

        const role =
            match?.role ||
            match?.projectRole ||
            resource?.role ||
            resource?.projectRole ||
            resource?.jobTitle;

        if (role === "Lead Attorney" || role === "Referring Attorney") {
            return role;
        }
        return "Member";
    };

    const stateResourceList = location.state?.resourceList || [];
    const isFromAddButton = location.state?.fromAddButton;

    useEffect(() => {
        if (!open) {
            form.resetFields();
            setStartDateValue(null); // ✅ clear tracked start date
            return;
        }
        if (editingResource) {
            form.setFieldsValue({
                selected_resource: editingResource.name,
                lead_attorney: leadAttorneyName || editingResource.name,
                charge_out_rate: String(editingResource.chargeOut || "").replace("$", "").replace("/hr", ""),
                person_day: editingResource.mandays,
                project_role:
                    editingResource.role ||
                    resolveRole(editingResource, stateResourceList),
                booked_start_date: editingResource.startDate
                    ? dayjs(editingResource.startDate)
                    : null,
                booked_finish_date: editingResource.finishDate
                    ? dayjs(editingResource.finishDate)
                    : null,
                justification:
                    editingResource?.raw?.justification ||
                    editingResource?.justification ||
                    undefined,
            });

            // Recalculate working days for edit
            if (editingResource.startDate && editingResource.finishDate) {
                const days = getWorkingDays(editingResource.startDate, editingResource.finishDate);
                setWorkingDays(days);
            }
        }
        else if (selectedResource) {
            const start = toSafeDayjs(planStartDate);
            const end = toSafeDayjs(planEndDate);

            form.setFieldsValue({
                selected_resource: selectedResource.name,
                lead_attorney: leadAttorneyName || selectedResource.name,
                charge_out_rate:
                    selectedResource.resourceExtRate ||
                    selectedResource.chargeOutRate ||
                    "",
                project_role: resolveRole(selectedResource, stateResourceList),
                person_day: "",
                booked_start_date: start || null,
                booked_finish_date: end || null,
                justification: undefined,
            });

            // Recalculate for defaults
            if (start && end) {
                const days = getWorkingDays(start, end);
                setWorkingDays(days);
            }
        }
    }, [open, editingResource, selectedResource, form]);

    // FIX 12: Ensure plan date restrictions are applied when dates change
    useEffect(() => {
        if (!open) return;

        const start = toSafeDayjs(planStartDate);
        const end = toSafeDayjs(planEndDate);

        if (!start && !end) return;

        const currentStart = toSafeDayjs(form.getFieldValue("booked_start_date"));
        const currentEnd = toSafeDayjs(form.getFieldValue("booked_finish_date"));

        let needsUpdate = false;
        const updates = {};

        // If current start is outside plan range, reset to plan start
        if (
            (currentStart && start && currentStart.isBefore(start, "day")) ||
            (currentStart && end && currentStart.isAfter(end, "day"))
        ) {
            updates.booked_start_date = start || null;
            needsUpdate = true;
        }

        // If current end is outside plan range, reset to plan end
        if (
            (currentEnd && start && currentEnd.isBefore(start, "day")) ||
            (currentEnd && end && currentEnd.isAfter(end, "day"))
        ) {
            updates.booked_finish_date = end || null;
            needsUpdate = true;
        }

        if (needsUpdate) {
            form.setFieldsValue(updates);
        }
    }, [planStartDate, planEndDate, open]);

    useEffect(() => {
        if (!open || !editingResource) return;
        const timer = setTimeout(() => {
            form.setFieldsValue({
                selected_resource: editingResource.name,
                lead_attorney: leadAttorneyName || editingResource.name,
                charge_out_rate: String(editingResource.chargeOut || "").replace("$", "").replace("/hr", ""),
                person_day: editingResource.mandays,
                project_role:
                    editingResource.role ||
                    resolveRole(editingResource, stateResourceList),
                booked_start_date: editingResource.startDate
                    ? dayjs(editingResource.startDate)
                    : null,
                booked_finish_date: editingResource.finishDate
                    ? dayjs(editingResource.finishDate)
                    : null,
                justification:
                    editingResource?.raw?.justification ||
                    editingResource?.justification ||
                    undefined,
            });
        }, 0);

        return () => clearTimeout(timer);
    }, [open, editingResource, form]);



    const recalc = () => {
        const start = form.getFieldValue("booked_start_date");
        const end = form.getFieldValue("booked_finish_date");
        const days = getWorkingDays(start, end);
        setWorkingDays(days);
        form.validateFields(["booked_start_date", "booked_finish_date", "person_day"]);
    };

    // ─── Submit ──────────────────────────────────────────────────────────────
    const onFinish = async (values) => {
        setLoading(true);

        try {
            let start = dayjs(values.booked_start_date);
            let end = dayjs(values.booked_finish_date);

            if (!start.isValid() || !end.isValid()) {
                message.error("Invalid date selection");
                setLoading(false);
                return;
            }

            if (end.isBefore(start)) {
                message.error("End date must be after start date");
                setLoading(false);
                return;
            }

            // validate plan range (frontend only)
            const planStart = toSafeDayjs(planStartDate);
            const planEnd = toSafeDayjs(planEndDate);

            if (planStart && start.isBefore(planStart, "day")) {
                message.error(
                    `Start date cannot be before plan start (${planStart.format("DD-MM-YYYY")})`
                );
                setLoading(false);
                return;
            }

            if (planEnd && end.isAfter(planEnd, "day")) {
                message.error(
                    `End date cannot be after plan end (${planEnd.format("DD-MM-YYYY")})`
                );
                setLoading(false);
                return;
            }

            // =========================
            // FRONTEND ONLY
            // No API call here
            // Only pass updated row to parent handleSubmit
            // =========================

            const resourceSource = editingResource?.raw || selectedResource;

            const chargeOutRate =
                resourceSource?.resourceExtRate ||
                resourceSource?.chargeOutRate ||
                parseFloat(
                    String(
                        editingResource?.chargeOut ||
                        selectedResource?.chargeOut ||
                        "0"
                    ).replace(/[^0-9.]/g, "")
                ) ||
                0;

            const updatedRow = {
                key:
                    editingResource?.key ||
                    selectedResource?.userId ||
                    `local-${Date.now()}`,

                // Preserve resourceId when editing an existing resource;
                // leave null for brand-new additions (backend will assign one).
                resourceId:
                    editingResource?.resourceId ||
                    editingResource?.raw?.resourceId ||
                    null,

                userId:
                    editingResource?.userId ||
                    selectedResource?.userId,

                employeeId:
                    editingResource?.raw?.employeeId ||
                    selectedResource?.employeeId,

                name:
                    editingResource?.name ||
                    selectedResource?.name ||
                    selectedResource?.employeeName,

                role:
                    values.project_role ||
                    editingResource?.role,

                mandays: Number(values.person_day || 0),

                chargeOut: `$${chargeOutRate}`,
                chargeOutRate: chargeOutRate,        // ✅ ADD: top-level for easy access
                resourceExtRate: chargeOutRate,
                startDate: start.format("YYYY-MM-DD"),
                finishDate: end.format("YYYY-MM-DD"),

                bookedHours: (Number(values.person_day || 0) * 8).toFixed(2),

                totalPcoValue:
                    Number(values.person_day || 0) *
                    8 *
                    Number(chargeOutRate || 0),

                totalPco: `$${(
                    Number(values.person_day || 0) *
                    8 *
                    Number(chargeOutRate || 0)
                ).toFixed(2)}`,

                status: "Assigned",
                justification: values.justification || "",
                bookingReset: false,   // ✅ clear reset flag so merge logic honours new dates
                raw: {
                    ...(editingResource?.raw || {}),
                    ...(selectedResource || {}),
                    // Preserve resourceId so Creatematter.js can read it via r.raw.resourceId
                    resourceId:
                        editingResource?.resourceId ||
                        editingResource?.raw?.resourceId ||
                        null,
                    actualBookedStartDate: start.format("YYYY-MM-DD"),
                    actualBookedEndDate: end.format("YYYY-MM-DD"),
                    personDay: Number(values.person_day || 0),
                    resourceExtRate: chargeOutRate,
                    chargeOutRate: chargeOutRate,
                    projectRole: values.project_role,
                    status: "Assigned",
                    justification: values.justification || "",
                },
            };

            // send only to parent
            // Use onUpdate when editing an existing resource (including after reset),
            // so the existing row is replaced rather than a duplicate being appended.
            if (editingResource) {
                onUpdate?.(updatedRow);
            } else {
                onAdd?.(updatedRow);
            }

            notification.success({
                message: editingResource
                    ? "Resource Updated Successfully"
                    : "Resource Added Successfully",
            });

            onClose();
        } catch (err) {
            console.error(err);

            notification.error({
                message: "Something went wrong. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    const jobTitle =
        selectedResource?.jobTitle ||
        matterData?.jobTitle ||
        "Add Resource";

    // FIX 12: Disabled date functions strictly enforce plan date range
    const disabledStartDate = (current) => {
        if (!current) return false;

        const start = toSafeDayjs(planStartDate);
        const end = toSafeDayjs(planEndDate);
        const selectedStart = toSafeDayjs(form.getFieldValue("booked_start_date"));

        // Allow already selected date (edit safety)
        if (selectedStart && current.isSame(selectedStart, "day")) {
            return false;
        }

        // Restrict to plan range — STRICT enforcement for both add & edit
        if (start && current.isBefore(start, "day")) return true;
        if (end && current.isAfter(end, "day")) return true;

        return false;
    };

    const disabledEndDate = (current) => {
        if (!current) return false;

        const start = toSafeDayjs(planStartDate);
        const end = toSafeDayjs(planEndDate);

        const selectedStart = toSafeDayjs(form.getFieldValue("booked_start_date"));
        const selectedEnd = toSafeDayjs(form.getFieldValue("booked_finish_date"));

        // Allow already selected end date
        if (selectedEnd && current.isSame(selectedEnd, "day")) {
            return false;
        }

        // Restrict to plan range
        if (start && current.isBefore(start, "day")) return true;
        if (end && current.isAfter(end, "day")) return true;

        // End must be after selected start
        if (selectedStart && current.isBefore(selectedStart, "day")) return true;

        return false;
    };

    return (
        <StyledModal
            open={open}
            onCancel={onClose}
            width={700}
            title={editingResource ? jobTitle : jobTitle}
        // destroyOnClose
        >
            <ModalBody>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    validateTrigger="onSubmit"
                // preserve={false}
                >
                    <Row gutter={[16, 12]}>
                        {/* Resource name — read-only */}
                        <Col span={12}>
                            <Form.Item name="lead_attorney" label="Lead Attorney">
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        {/* Resource — read-only */}
                        <Col span={12}>
                            <Form.Item name="selected_resource" label="Resource">
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        {/* Booked start date */}
                        <Col span={12}>
                            <Form.Item
                                name="booked_start_date"
                                label="Start Date"
                                rules={[{ required: true, message: "Required" }]}
                            >
                                <DatePicker
                                    onChange={(val) => {
                                        setStartDateValue(val);   // ✅ track for end-picker navigation
                                        recalc(val);
                                    }}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledStartDate}
                                    // FIX: removed `disabled={isActiveMatter}` — it was hard-locking
                                    // the picker regardless of plan range, so dates could never be
                                    // expanded while editing. disabledDate already enforces the
                                    // plan's valid range, so no separate hard disable is needed here.
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Booked finish date */}
                        <Col span={12}>
                            <Form.Item
                                name="booked_finish_date"
                                label="End Date"
                                rules={[
                                    { required: true, message: "Required" },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            const start = getFieldValue("booked_start_date");
                                            if (value && start && value.isBefore(start)) {
                                                return Promise.reject("End must be after start");
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    onChange={recalc}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledEndDate}
                                    // FIX: removed `disabled={isActiveMatter}` (see start date above)
                                    defaultPickerValue={startDateValue || undefined}  // ✅ jump to start month
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Working days display */}
                        <Col span={24}>
                            <span className="mandays-label">
                                Total Booked Mandays: {workingDays}
                            </span>
                        </Col>

                        {/* Budget mandays */}
                        <Col span={12}>
                            <Form.Item
                                name="person_day"
                                label="Budget Mandays"
                                validateTrigger={["onChange", "onBlur"]}
                                rules={[
                                    { required: true, message: "Required" },
                                    {
                                        validator(_, v) {
                                            if (!v && v !== 0) return Promise.resolve();
                                            const num = Number(v);
                                            if (num <= 0)
                                                return Promise.reject("Must be greater than 0");
                                            if (workingDays > 0 && num > workingDays)
                                                return Promise.reject(
                                                    `Cannot exceed ${workingDays} working days`
                                                );
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    type="number"
                                    min={0.5}
                                    step={0.5}
                                    onChange={() => {
                                        form.validateFields(["person_day"]);
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        {/* Role */}
                        <Col span={12}>
                            <Form.Item
                                name="project_role"
                                label="Role"
                                rules={[{ required: true, message: "Required" }]}
                            >
                                <Select
                                    disabled
                                    options={ROLE_OPTIONS}
                                    placeholder="Select role"
                                />
                            </Form.Item>
                        </Col>

                        {/* Justification */}
                        <Col span={24}>
                            <Form.Item
                                name="justification"
                                label="Justification"
                                rules={[
                                    { required: true, message: "Please select a justification" },
                                ]}
                            >
                                <Select placeholder="Select a justification">
                                    {JUSTIFICATION_OPTIONS.map((j) => (
                                        <Option key={j} value={j}>
                                            {j}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="footer-btns">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-proceed" disabled={loading}>
                            {loading ? "Saving..." : "Proceed"}
                        </button>
                    </div>
                </Form>
            </ModalBody>
        </StyledModal>
    );
};

export default AddResourceModal;